// ============================================================
// Chess Arena — Battle Event Wiring
// ============================================================
// Owns all EventBus handler registrations and cleanup for
// BattleScene. Does NOT own system instantiation — BattleScene
// creates all systems; BattleEventWiring receives them and
// wires the events.
// ============================================================

import eventBus from '../core/eventBus';
import { GridPos } from '../core/types';
import { PieceManager } from './pieceManager';
import { SpawnSystem } from './spawnSystem';
import { AbilityEffectHandler } from './abilityEffectHandler';

// -----------------------------------------------------------------
// Config
// -----------------------------------------------------------------

export interface BattleEventWiringConfig {
  pieceManager: PieceManager;
  spawnSystem: SpawnSystem;
  abilityEffectHandler: AbilityEffectHandler;
  getElapsedTimeSec: () => number;
  onMatchEnd: (data?: Record<string, unknown>) => void;
  onTimerExpired: () => void;
}

// -----------------------------------------------------------------
// Handler type alias
// -----------------------------------------------------------------

type Handler = (data?: Record<string, unknown>) => void;

// -----------------------------------------------------------------
// BattleEventWiring
// -----------------------------------------------------------------

export class BattleEventWiring {
  // Stored handler references for cleanup
  private _timerExpiredHandler: Handler | null = null;
  private _premoveHandler: Handler | null = null;
  private _moveArrivalHandler: Handler | null = null;
  private _scriptedMoveHandler: Handler | null = null;
  private _matchEndHandler: Handler | null = null;
  private _toolUsedHandler: Handler | null = null;
  private _bombHandler: Handler | null = null;
  private _freezeHandler: Handler | null = null;
  private _shieldHandler: Handler | null = null;
  private _lightningHandler: Handler | null = null;
  private _targetingStartHandler: Handler | null = null;
  private _targetingCancelHandler: Handler | null = null;

  // ------------------------------------------------------------------
  // wireAll — register all event handlers on eventBus
  // ------------------------------------------------------------------

  wireAll(config: BattleEventWiringConfig): void {
    // Always clean up first to prevent duplicate registrations
    this.cleanup();

    const {
      pieceManager,
      spawnSystem,
      abilityEffectHandler,
      onMatchEnd,
      onTimerExpired,
    } = config;

    // --- Timer expired → defeat ---
    this._timerExpiredHandler = () => {
      onTimerExpired();
    };
    eventBus.on('timer_expired', this._timerExpiredHandler);

    // --- Premove execution ---
    this._premoveHandler = (data) => {
      if (!data) return;
      const pieceId = data.pieceId as string;
      const target = data.target as GridPos;
      const piece = pieceManager.getPieceById(pieceId);
      if (piece && piece.pieceState.isAlive) {
        piece.setPremove(null); // Clear premove before executing to prevent re-trigger
        pieceManager.executeMove(piece, target);
      }
    };
    eventBus.on('premove_executed', this._premoveHandler);

    // --- Piece arrival → resolve capture on landing ---
    this._moveArrivalHandler = (data) => {
      if (!data) return;
      const pieceId = data.pieceId as string;
      const piece = pieceManager.getPieceById(pieceId);
      if (piece && piece.pieceState.isAlive) {
        pieceManager.resolveMoveArrival(piece);
      }
    };
    eventBus.on('piece_moved_complete', this._moveArrivalHandler);

    // --- Tutorial scripted moves ---
    this._scriptedMoveHandler = (data) => {
      if (!data) return;
      const moveData = data as { piece: string; destination: { col: number; row: number } };
      pieceManager.executeScriptedMove(moveData.piece, moveData.destination);
    };
    // tutorial_scripted_move is not in the EventName union; cast required
    eventBus.on('tutorial_scripted_move' as any, this._scriptedMoveHandler);

    // --- Match end (from skip button or other sources) ---
    this._matchEndHandler = (data) => {
      onMatchEnd(data);
    };
    eventBus.on('match_end', this._matchEndHandler);

    // --- Tool used → delegate to SpawnSystem ---
    this._toolUsedHandler = (data?: Record<string, unknown>) => {
      const toolId = data?.tool as string;
      if (toolId === 'reroll') {
        spawnSystem.rerollPreview('player');
      } else if (toolId === 'instant_spawn') {
        spawnSystem.instantSpawn('player');
      }
    };
    eventBus.on('tool_used', this._toolUsedHandler);

    // --- Ability effects ---
    this._bombHandler = () => {
      abilityEffectHandler.handleBombEffect();
    };
    eventBus.on('ability_bomb', this._bombHandler);

    this._freezeHandler = (data?: Record<string, unknown>) => {
      abilityEffectHandler.handleFreezeEffect(data, config.getElapsedTimeSec());
    };
    eventBus.on('ability_freeze', this._freezeHandler);

    this._shieldHandler = (data?: Record<string, unknown>) => {
      abilityEffectHandler.handleShieldEffect(data, config.getElapsedTimeSec());
    };
    eventBus.on('ability_shield', this._shieldHandler);

    this._lightningHandler = (data?: Record<string, unknown>) => {
      abilityEffectHandler.handleLightningEffect(data);
    };
    eventBus.on('ability_lightning', this._lightningHandler);

    // --- Targeting mode ---
    this._targetingStartHandler = (_data?: Record<string, unknown>) => {
      abilityEffectHandler.enterTargetingMode();
    };
    eventBus.on('targeting_mode_start', this._targetingStartHandler);

    this._targetingCancelHandler = () => {
      abilityEffectHandler.exitTargetingMode();
    };
    eventBus.on('targeting_mode_cancel', this._targetingCancelHandler);
  }

  // ------------------------------------------------------------------
  // cleanup — remove all event handlers from eventBus
  // ------------------------------------------------------------------

  cleanup(): void {
    if (this._timerExpiredHandler) {
      eventBus.off('timer_expired', this._timerExpiredHandler);
      this._timerExpiredHandler = null;
    }
    if (this._premoveHandler) {
      eventBus.off('premove_executed', this._premoveHandler);
      this._premoveHandler = null;
    }
    if (this._moveArrivalHandler) {
      eventBus.off('piece_moved_complete', this._moveArrivalHandler);
      this._moveArrivalHandler = null;
    }
    if (this._scriptedMoveHandler) {
      eventBus.off('tutorial_scripted_move' as any, this._scriptedMoveHandler);
      this._scriptedMoveHandler = null;
    }
    if (this._matchEndHandler) {
      eventBus.off('match_end', this._matchEndHandler);
      this._matchEndHandler = null;
    }
    if (this._toolUsedHandler) {
      eventBus.off('tool_used', this._toolUsedHandler);
      this._toolUsedHandler = null;
    }
    if (this._bombHandler) {
      eventBus.off('ability_bomb', this._bombHandler);
      this._bombHandler = null;
    }
    if (this._freezeHandler) {
      eventBus.off('ability_freeze', this._freezeHandler);
      this._freezeHandler = null;
    }
    if (this._shieldHandler) {
      eventBus.off('ability_shield', this._shieldHandler);
      this._shieldHandler = null;
    }
    if (this._lightningHandler) {
      eventBus.off('ability_lightning', this._lightningHandler);
      this._lightningHandler = null;
    }
    if (this._targetingStartHandler) {
      eventBus.off('targeting_mode_start', this._targetingStartHandler);
      this._targetingStartHandler = null;
    }
    if (this._targetingCancelHandler) {
      eventBus.off('targeting_mode_cancel', this._targetingCancelHandler);
      this._targetingCancelHandler = null;
    }
  }
}
